import {RadioOption} from '@/types/index'
import usei18nExtended from '@/composables/usei18nExtended'
import {useSimmerringSelectorStore} from '@/store/simmerring-selector'
import {components} from '@/types/simmerring'
import {ComputedRef} from 'vue'
import {SIMMERRING_STORE_CONSTANTS} from '~/assets/js/constants'
import {IS_WIDGET, WIDGET_CONFIG} from '~/widgets/shared/widget-config'

export const useSimmerringHelper = () => {
  const i18n = usei18nExtended()
  const simmerringSelectorStore = useSimmerringSelectorStore()
  const isWidget = inject(IS_WIDGET, false)
  const widgetConfig = isWidget ? inject(WIDGET_CONFIG) : null
  const BASE_URL = widgetConfig?.assetBaseUrl ?? useRuntimeConfig().public.BASE_URL
  const {SIMM_MEASURE_INCH, SIMM_TEMP_CELSIUS} = SIMMERRING_STORE_CONSTANTS

  const {
    unitLength,
    unitTemperature,
    shaftDiameter,
    caseDiameter,
    width,
    rotationalSpeed,
    pressure,
    lubricant,
    temperature,
    tempDefinition,
    dirtContamination,
    finderMaterial,
    finderDesign,
    finderDirtContamination,
    metaData
  } = toRefs(simmerringSelectorStore)
  type ApiAssistantRequest = components['schemas']['ApiAssistantRequest']
  type ApiFinderRequest = components['schemas']['ApiFinderRequest']

  const getSeoOptions = (path = '') => {
    if (isWidget) {
      return {title: '', description: '', image: '', url: '', locale: ''}
    }
    const {locale} = i18n
    const region = useNuxtApp().$globalization.getRegion().toLowerCase()
    const seoPath = path ? `/${path}` : ''

    return {
      title: i18n.tv('ogTitle', 'simmerringSelector'),
      description: i18n.tv('ogDescription', 'simmerringSelector'),
      image: `${BASE_URL}/img/simmerring_og_image.jpg`,
      url: `${BASE_URL}/${region}/${locale.value}/simmerring-selector${seoPath}`,
      locale: locale.value
    }
  }

  const lubricantOptions: RadioOption[] = [
    {value: 'Mineral Oil', label: i18n.tv('mineralOil', 'accumulatorSelector')},
    {value: 'Synthetic Oil', label: i18n.tv('syntheticOil', 'accumulatorSelector')},
    {value: 'Fat', label: i18n.tv('grease', 'simmerringSelector')}
  ]

  const temperatureOptions: RadioOption[] = [
    {value: 'fluidTemp', label: i18n.tv('fluidTemp', 'simmerringSelector')},
    {value: 'contactTemp', label: i18n.tv('contactTemp', 'simmerringSelector')}
  ]

  const dirtContanimationOptions: RadioOption[] = [
    {value: 'None', label: i18n.tv('none', 'simmerringSelector')},
    {value: 'Low', label: i18n.tv('low', 'simmerringSelector')},
    {value: 'Strong', label: i18n.tv('high', 'hydraulicSelector')}
  ]

  const finderDirtContaminationOptions: RadioOption[] = [
    {value: null, label: i18n.tv('noPreference', 'simmerringSelector')},
    {value: 'yes', label: i18n.tv('yes', '')},
    {value: 'no', label: i18n.tv('no', '')}
  ]

  const materialOptions: ComputedRef<RadioOption[]> = computed(() => {
    if (!metaData.value) {
      simmerringSelectorStore.getMetaData()
    }
    return [
      {value: null, label: i18n.tv('noPreference', 'simmerringSelector')},
      ...metaData.value?.materials?.map((material: any) => ({
        value: material,
        label: material
      })) ?? []
    ]
  })

  const designOptions: ComputedRef<RadioOption[]> = computed(() => {
    if (!metaData.value) {
      simmerringSelectorStore.getMetaData()
    }
    return [
      {value: null, label: i18n.tv('noPreference', 'simmerringSelector'), imageUrl: ''},
      ...metaData.value?.designs?.map((design: any) => ({
        value: design,
        label: (design.toLowerCase() === 'babsl' || design.toLowerCase() === 'pps')
          ? `${design} </br> ${i18n.tv('pressureDesign', 'simmerringSelector')}`
          : design,
        imageUrl: i18n.tv(design.toLowerCase(), 'categoryImageLink')
      })) ?? []
    ]
  })

  const performUnitLengthSwitch = (unit: string) => {
    simmerringSelectorStore.setUnitLength(unit)
    shaftDiameter.value = convertValue(unit, shaftDiameter.value)
    caseDiameter.value = convertValue(unit, caseDiameter.value)
    width.value = convertValue(unit, width.value)
  }

  const performUnitTempSwitch = (unit: string) => {
    temperature.value = convertTempValue(unit, temperature.value)
  }

  const convertValue = (unit: string, value: any) => {
    if (!value) {
      return null
    }
    if (unit === SIMM_MEASURE_INCH) {
      return roundNumber(value / 25.4, 3)
    }

    return roundNumber(value * 25.4, 0)
  }

  const convertTempValue = (unit: string, value: any) => {
    if (!value) {
      return null
    }
    if (unit === SIMM_TEMP_CELSIUS) {
      return roundNumber((value - 32) * (5 / 9))
    }

    return roundNumber(value * (9 / 5) + 32)
  }

  const roundNumber = (number: number, decimalPoints: number = 2) => {
    return parseFloat(number.toFixed(decimalPoints))
  }

  const convertStoreValuesToApiAssistantRequest = (): ApiAssistantRequest => {
    const finderRequest: ApiAssistantRequest = {
      metricSystem: unitLength.value,
      temperatureSystem: unitTemperature.value,
      shaftDiameter: shaftDiameter.value,
      caseDiameter: caseDiameter.value,
      width: width.value,
      maxNumOfRevs: rotationalSpeed.value,
      temperature: temperature.value,
      isContactTemp: tempDefinition.value === 'contactTemp',
      pressure: pressure.value,
      fluid: lubricant.value ?? '',
      dirtLip: dirtContamination.value ?? ''
    }

    return finderRequest
  }

  const convertStoreValuesToApiFinderRequest = (): ApiFinderRequest => {
    let dustlip: boolean | null
    if (finderDirtContamination.value === 'yes') {
      dustlip = true
    } else if (finderDirtContamination.value === 'no') {
      dustlip = false
    } else {
      dustlip = null
    }

    return {
      metricSystem: unitLength.value,
      shaftDiameter: shaftDiameter.value,
      caseDiameter: caseDiameter.value,
      width: width.value,
      material: finderMaterial.value ?? '',
      design: finderDesign.value ?? '',
      dustlip
    }
  }

  return {
    lubricantOptions,
    temperatureOptions,
    dirtContanimationOptions,
    materialOptions,
    designOptions,
    finderDirtContaminationOptions,
    performUnitLengthSwitch,
    performUnitTempSwitch,
    convertStoreValuesToApiAssistantRequest,
    convertStoreValuesToApiFinderRequest,
    getSeoOptions
  }
}
